import { PHLoading } from '_/components';
import { colors } from '_/constants/theme';
import { useEventContext } from '_/hooks/EventContext';
import { useLanguage } from '_/hooks/LanguageContext';
import { useSlotContext } from '_/hooks/SlotContext';
import { AppRoute } from '_/navigation/types';
import { eventsApi, roleAccessesApi } from '_/services/api';
import logger from '_/services/logger';
import { NavigationProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { showMessage } from 'react-native-flash-message';
import styles from './styles';
import useFirebaseAnalytics from '_/hooks/useFirebaseAnalytics';
import { networkErrorMessage } from '_/helpers/networkError';
import { checkinErrorMessage } from '_/utils/checkinErros';
import { SpaceType } from '_/services/models/enums/space-type.enum';
import { isBefore, parseISO, subMinutes } from 'date-fns';
import { useSpaceContext } from '_/hooks/SpaceContext';
import { SlotsModel } from '_/services/models/slots.model';
import { useAuth } from '_/hooks/AuthContext';
import { weekDayOf, weekDaysAllowed } from '_/utils/weekDays';
import { WEEK_DAY_I18N_KEY, WeekDay } from '_/services/models/enums/week-day.enum';

interface ParamsProps {
  slotId: string;
}

export default function ReadingQRCode() {
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp<Record<string, unknown>>>();
  const route = useRoute();
  const { slotId } = (route.params ?? {}) as ParamsProps;
  const { getSpaceById } = useSpaceContext();
  const { getTodayEvents } = useEventContext();
  const { getConfirmedReservationForSlot } = useEventContext();
  const { member } = useAuth();
  const { locale } = useLanguage();
  const { analyticsLogEvent } = useFirebaseAnalytics();

  useEffect(() => {
    if (!slotId) {
      showMessage({
        message: t('error'),
        description: t('readingQrCodeScreen.invalidQrCode'),
        backgroundColor: colors.errorRed,
      });
      navigation.navigate(AppRoute.HOME);
      return;
    }

    handleSlotCheckin(slotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSlotCheckin(slotId: string) {
    let slot;
    try {
      const memberRolesId = member?.roles?.map((role) => role.id);
      const { data: roleAccesses } = await roleAccessesApi.getList({
        slotId,
      });
      slot = roleAccesses[0]?.slot as SlotsModel;
      const hasAccess = roleAccesses.some((roleAccess) =>
        memberRolesId?.includes(roleAccess.roleId)
      );

      const weekDay = weekDayOf(new Date());
      const allowedWeekDays = weekDaysAllowed(roleAccesses);
      const disallowedDay = !allowedWeekDays.includes(weekDay);
      if (!hasAccess || disallowedDay) {
        const hasWeekDayAccess = allowedWeekDays.length;
        let msg = t('readingQrCodeScreen.noAccess');
        if (hasAccess && hasWeekDayAccess && disallowedDay)
          msg = t('workstationScreen.errorMessages.weekDaysNotAllowed', {
            weekDays: allowedWeekDays
              .map((weekDay: WeekDay) => t(WEEK_DAY_I18N_KEY[weekDay]))
              .join(', '),
          });
        if (hasAccess && !hasWeekDayAccess) msg = t('readingQrCodeScreen.noDayAccess');

        showMessage({
          message: t('error'),
          description: msg,
          backgroundColor: colors.errorRed,
        });
        navigation.navigate(AppRoute.HOME);
        return;
      }
    } catch (error) {
      logger(error);
      showMessage({
        message: t('error'),
        description: t('readingQrCodeScreen.invalidQrCode'),
        backgroundColor: colors.errorRed,
      });
      navigation.navigate(AppRoute.HOME);
      return;
    }

    const reservation = await getConfirmedReservationForSlot(slotId);

    if (!reservation) {
      navigation.navigate(AppRoute.QR_RESERVATION, { slot });
      return;
    }

    try {
      const space = await getSpaceById(slot.spaceId);
      if (space?.type === SpaceType.WORK) {
        const now = new Date();
        const maxEarlyCheckinOffset = space?.maxEarlyCheckinOffset;
        const blockEarlyCheckin = isBefore(
          now,
          subMinutes(parseISO(reservation.startAt), maxEarlyCheckinOffset ?? 30)
        );
        if (blockEarlyCheckin)
          return navigation.navigate(AppRoute.CHECKIN_SUCCESS, {
            event: reservation,
            blockEarlyCheckin: true,
            maxEarlyCheckinOffset: maxEarlyCheckinOffset,
          });
      }
      const checkedInEvent = await eventsApi.checkinByQrCode({ id: reservation.id, slotId });
      navigation.navigate(AppRoute.CHECKIN_SUCCESS, { event: checkedInEvent });
      analyticsLogEvent({
        name: 'makeCheckin',
        properties: {
          name: 'makeCheckin',
          screen: 'ReadingQRCode',
          purpose: 'Checkin event',
        },
      });
      await getTodayEvents();
    } catch (error: any) {
      analyticsLogEvent({
        name: 'errorMakeCheckin',
        properties: {
          name: 'errorMakeCheckin',
          screen: 'ReadingQRCode',
          purpose: 'Checkin event',
        },
      });
      networkErrorMessage(error);
      const errorData = error?.response?.data?.data;
      checkinErrorMessage(errorData, t, locale);
      navigation.navigate(AppRoute.HOME);
    }
  }

  return (
    <View style={styles.container}>
      <PHLoading loading type="logo" message={t('readingQrCodeScreen.loading')} />
    </View>
  );
}
